import { withAnnotations, withCaption, type V2Evidence } from '../domain/evidence';
import { annotationsOf, type PhotoAnnotation } from '../domain/annotation';
import { putRecords } from '../persistence/v2Database';
import { createStore } from './createStore';
import { assertInspectionUnlocked } from './inspectionStore';

// Evidence/photo record state. Photos are their own domain: this store never knows about
// checklist rows or findings — they hold LINKS to evidence ids (three-layer model,
// OWNER-CONTRACT §6.2). Bytes live in the V2 media store and are resolved to object URLs
// by src/state/mediaUrls.ts. All lane orchestration (camera → album → persist → link) is in
// src/state/captureFlow.ts, bound by src/native/mediaContract.ts.
export interface EvidenceState {
  hydrated: boolean;
  evidence: V2Evidence[];
}

export const evidenceStore = createStore<EvidenceState>({ hydrated: false, evidence: [] });

export function resetEvidenceStoreForTests(): void {
  evidenceStore.set(() => ({ hydrated: false, evidence: [] }));
}

export function applyEvidenceSnapshot(evidence: V2Evidence[]): void {
  evidenceStore.set(() => ({ hydrated: true, evidence: [...evidence] }));
}

export function upsertEvidenceLocal(record: V2Evidence): void {
  evidenceStore.set((prev) => {
    const exists = prev.evidence.some((e) => e.id === record.id);
    return {
      ...prev,
      evidence: exists ? prev.evidence.map((e) => (e.id === record.id ? record : e)) : [...prev.evidence, record]
    };
  });
}

export function removeEvidenceLocal(evidenceId: string): void {
  evidenceStore.set((prev) => ({ ...prev, evidence: prev.evidence.filter((e) => e.id !== evidenceId) }));
}

export function removeEvidenceOfInspectionLocal(inspectionId: string): void {
  evidenceStore.set((prev) => ({ ...prev, evidence: prev.evidence.filter((e) => e.inspectionId !== inspectionId) }));
}

// ---------- selectors ----------

export function evidenceOfArea(state: EvidenceState, areaId: string): V2Evidence[] {
  return state.evidence.filter((e) => e.areaId === areaId);
}

// Photos imported into the pool without a capture area (bulk import, task book §9).
export function poolOnlyEvidenceOf(state: EvidenceState, inspectionId: string): V2Evidence[] {
  return state.evidence.filter((e) => e.inspectionId === inspectionId && e.areaId === null);
}

export function evidenceOfInspection(state: EvidenceState, inspectionId: string): V2Evidence[] {
  return state.evidence.filter((e) => e.inspectionId === inspectionId);
}

export function evidenceForIds(state: EvidenceState, ids: readonly string[]): V2Evidence[] {
  const byId = new Map(state.evidence.map((e) => [e.id, e] as const));
  return ids.map((id) => byId.get(id)).filter((e): e is V2Evidence => e !== undefined);
}

// ---------- actions that only touch the evidence record ----------

// 2026-10-19 (Owner item 22): the marks drawn on a photo; the image itself is never changed.
export async function setEvidenceAnnotationsAction(evidenceId: string, annotations: PhotoAnnotation[], now: number = Date.now()): Promise<void> {
  const current = evidenceStore.get().evidence.find((e) => e.id === evidenceId);
  if (!current) return;
  assertInspectionUnlocked(current.inspectionId);
  const next = withAnnotations(current, annotationsOf(annotations), now);
  await putRecords([{ store: 'evidence', value: next }]);
  upsertEvidenceLocal(next);
}

export async function setEvidenceCaptionAction(evidenceId: string, caption: string, now: number = Date.now()): Promise<void> {
  const current = evidenceStore.get().evidence.find((e) => e.id === evidenceId);
  if (!current) return;
  assertInspectionUnlocked(current.inspectionId);
  const next = withCaption(current, caption, now);
  await putRecords([{ store: 'evidence', value: next }]);
  upsertEvidenceLocal(next);
}
